/**
 * 環境変数ユーティリティ
 * アプリケーション全体で一貫した環境変数の取得と型安全性を提供
 */

import logger from './logger';

/**
 * デモモードが有効かどうかを判定
 * @returns true: デモモード, false: 本番モード
 */
export const isDemoMode = (): boolean => {
  const demoMode = import.meta.env.VITE_DEMO_MODE;
  return demoMode === 'true';
};

// isMockMode() は削除されました。
// デモモード判定には isDemoMode() を使用してください。

/**
 * デバッグモードが有効かどうかを判定
 * @returns true: デバッグモード有効, false: 無効
 */
export const isDebugMode = (): boolean => {
  const debugMode = import.meta.env.VITE_DEBUG_MODE;
  return debugMode === 'true';
};

/**
 * 本番環境かどうかを判定
 * @returns true: 本番環境, false: 開発環境
 */
export const isProduction = (): boolean => {
  return import.meta.env.PROD;
};

/**
 * デバッグログを出力（開発環境のみ）
 * @param message ログメッセージ
 * @param data 追加のデータ
 */
export const debugLog = (message: string, ...data: unknown[]): void => {
  if (!isProduction() && isDebugMode()) {
    logger.debug(message, { data });
  }
};

/**
 * デバッグエラーログを出力（開発環境のみ）
 * @param message エラーメッセージ
 * @param error エラーオブジェクト
 */
export const debugError = (message: string, error?: Error | unknown): void => {
  if (!isProduction() && isDebugMode()) {
    logger.error(message, { error });
  }
};

/**
 * Gemini API キーを取得
 * @returns Gemini API キー、または undefined
 *
 * 優先順位（後方互換性のため）:
 * 1. VITE_GEMINI_API_KEY（推奨）
 * 2. GEMINI_API_KEY（レガシー）
 * 3. API_KEY（レガシー）
 */
export const getGeminiApiKey = (): string | undefined => {
  return (
    import.meta.env.VITE_GEMINI_API_KEY || import.meta.env.GEMINI_API_KEY || import.meta.env.API_KEY
  );
};

/**
 * Supabase設定を取得
 * @returns Supabase URL と Anonymous Key
 */
export const getSupabaseConfig = () => {
  const config = {
    url: import.meta.env.VITE_SUPABASE_URL,
    anonKey: import.meta.env.VITE_SUPABASE_ANON_KEY,
  };

  if (isDebugMode()) {
    logger.debug('[Environment] Supabase設定確認:', {
      urlLength: config.url?.length || 0,
      anonKeyLength: config.anonKey?.length || 0,
      urlValid: !!config.url && config.url.startsWith('https://'),
      anonKeyValid: !!config.anonKey && config.anonKey.startsWith('eyJ'),
    });
  }

  return config;
};

/**
 * Supabase接続先環境の型定義
 */
export interface SupabaseTarget {
  type: 'staging' | 'production' | 'demo' | 'development' | 'unknown';
  label: string;
  icon: string;
  projectId: string;
}

/**
 * 環境情報の型定義
 */
export interface EnvironmentInfo {
  type: 'staging' | 'production' | 'demo' | 'development' | 'unknown';
  label: string;
  icon: string;
  color: 'blue' | 'green' | 'orange' | 'purple' | 'gray';
  projectId?: string;
  supabaseTarget?: SupabaseTarget;
}

/**
 * ローカル実行かどうかを判定
 * @returns true: ローカル実行, false: デプロイ済み
 */
export const isLocalExecution = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  const hostname = window.location.hostname;
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname.startsWith('192.168.') ||
    hostname.endsWith('.local')
  );
};

/**
 * Supabase URLから接続先環境を判定
 * @param url Supabase URL
 * @returns Supabase接続先情報
 */
export const getSupabaseTarget = (url?: string): SupabaseTarget => {
  if (!url) {
    return {
      type: 'unknown',
      label: '不明',
      icon: '❓',
      projectId: 'unknown',
    };
  }

  // URLからプロジェクトIDを抽出
  const match = url.match(/https:\/\/([a-z0-9-]+)\.supabase\.co/);
  const projectId = match ? match[1] : 'unknown';

  // 環境変数からタイプを決定
  const envType = import.meta.env.VITE_ENVIRONMENT_TYPE?.toLowerCase();

  // 明示的な環境タイプが設定されている場合はそれを使用
  if (envType) {
    switch (envType) {
      case 'staging':
        return {
          type: 'staging',
          label: 'ステージング',
          icon: '🔧',
          projectId,
        };
      case 'production':
        return {
          type: 'production',
          label: '本番',
          icon: '🚀',
          projectId,
        };
      case 'demo':
        return {
          type: 'demo',
          label: 'デモ',
          icon: '🧪',
          projectId,
        };
      case 'development':
        return {
          type: 'development',
          label: 'rakuhai',
          icon: '💻',
          projectId,
        };
    }
  }

  // フォールバック: プロジェクトIDから推測（後方互換性のため）
  // yzllkwwdwqwxuraypkro はステージング環境
  if (url.includes('yzllkwwdwqwxuraypkro')) {
    return {
      type: 'staging',
      label: 'ステージング',
      icon: '🔧',
      projectId: 'yzllkwwdwqwxuraypkro',
    };
  }

  return {
    type: 'unknown',
    label: '不明',
    icon: '❓',
    projectId,
  };
};

/**
 * Supabase URLから環境情報を取得
 * @returns 環境情報オブジェクト
 */
export const getEnvironmentInfo = (): EnvironmentInfo => {
  const url = import.meta.env.VITE_SUPABASE_URL;
  const isLocal = isLocalExecution();
  const supabaseTarget = getSupabaseTarget(url);

  // 環境変数で明示的に指定されている場合は、それを優先
  const envType = import.meta.env.VITE_ENVIRONMENT_TYPE?.toLowerCase();

  // ローカル実行の場合
  if (isLocal) {
    // ローカル実行時は「ローカル開発」として表示
    const baseInfo: EnvironmentInfo = {
      type: 'development',
      label: 'ローカル開発',
      icon: '💻',
      color: 'purple',
    };

    // rakuhai環境への接続の場合は、シンプルに表示
    if (supabaseTarget.type === 'development') {
      return {
        ...baseInfo,
        projectId: supabaseTarget.projectId,
      };
    }

    // 他のSupabase環境への接続の場合は、接続先も表示
    return {
      ...baseInfo,
      supabaseTarget,
      projectId: supabaseTarget.projectId,
    };
  }

  // デプロイ済み環境の場合、環境変数を優先
  if (envType) {
    switch (envType) {
      case 'staging':
        return {
          type: 'staging',
          label: '開発環境',
          icon: '🔧',
          color: 'blue',
          projectId: supabaseTarget.projectId,
        };
      case 'production':
        return {
          type: 'production',
          label: '本番環境',
          icon: '🚀',
          color: 'green',
          projectId: supabaseTarget.projectId,
        };
      case 'demo':
        return {
          type: 'demo',
          label: 'デモ体験',
          icon: '🧪',
          color: 'orange',
          projectId: supabaseTarget.projectId,
        };
      case 'development':
        return {
          type: 'development',
          label: 'ローカル開発',
          icon: '💻',
          color: 'purple',
          projectId: supabaseTarget.projectId,
        };
    }
  }

  // フォールバック: URLから判定（後方互換性のため）
  if (url?.includes('yzllkwwdwqwxuraypkro')) {
    return {
      type: 'staging',
      label: '開発環境',
      icon: '🔧',
      color: 'blue',
      projectId: 'yzllkwwdwqwxuraypkro',
    };
  }

  // 不明な環境
  return {
    type: 'unknown',
    label: '不明な環境',
    icon: '❓',
    color: 'gray',
  };
};

/**
 * 現在の環境タイプを取得
 * @returns 環境タイプ
 */
export const getCurrentEnvironmentType = (): EnvironmentInfo['type'] => {
  return getEnvironmentInfo().type;
};

/**
 * 指定した環境かどうかを判定
 * @param targetType チェックしたい環境タイプ
 * @returns true: 指定環境, false: その他
 */
export const isEnvironment = (targetType: EnvironmentInfo['type']): boolean => {
  return getCurrentEnvironmentType() === targetType;
};

/**
 * デモ環境かどうかを判定（既存のロジックを活用）
 * @returns true: デモ環境, false: その他
 */
export const isDemoEnvironment = (): boolean => {
  return isDemoMode() && isEnvironment('demo');
};

/**
 * テスト環境かどうかを判定
 * ブラウザ・Node.js両環境で動作
 * @returns true: テスト環境, false: その他
 */
export const isTestEnvironment = (): boolean => {
  // Node.js環境（統合テスト・Edge Functions）
  if (typeof process !== 'undefined' && process.env) {
    if (process.env.NODE_ENV === 'test') {
      return true;
    }
  }

  // ブラウザ環境（Vite）
  return (
    import.meta.env?.MODE === 'test' ||
    (typeof global !== 'undefined' && !!(global as any).__vitest__)
  );
};

/**
 * CI/CD環境かどうかを判定
 * ブラウザ・Node.js両環境で動作
 * @returns true: CI/CD環境, false: その他
 */
export const isCIEnvironment = (): boolean => {
  // Node.js環境（統合テスト・GitHub Actions等）
  if (typeof process !== 'undefined' && process.env) {
    return !!(
      process.env.CI ||
      process.env.GITHUB_ACTIONS ||
      process.env.GITLAB_CI ||
      process.env.JENKINS_URL ||
      process.env.CIRCLECI
    );
  }

  // ブラウザ環境ではCI/CDではないとみなす
  return false;
};

/**
 * Supabaseが利用可能かどうかを判定
 * @returns true: Supabase利用可能, false: スタブ実装使用
 */
export const isSupabaseAvailable = (): boolean => {
  const config = getSupabaseConfig();
  const isAvailable = !!(config.url && config.anonKey);

  // ローカルSupabase接続の検出と警告
  const isLocalhost =
    (config.url && config.url.includes('127.0.0.1')) ||
    (config.url && config.url.includes('localhost'));

  if (isLocalhost) {
    // CI/CDテスト環境では、ローカルSupabaseの使用を許可
    if (isCIEnvironment() && isTestEnvironment()) {
      logger.debug('🧪 CI/CDテスト環境: ローカルSupabase接続を許可');
      return isAvailable;
    }

    logger.error('🚨 ローカルSupabase接続が検出されました！');
    logger.error(
      '⚠️  リモートSupabaseを使用してください: 詳細は docs/supabase/configuration/environment-endpoints.md を参照'
    );
    logger.error('📝 .env.localファイルを確認し、VITE_SUPABASE_URLを修正してください');

    // 開発環境では警告のみ、本番環境では接続を拒否
    if (isProduction()) {
      logger.error('❌ 本番環境でのローカルSupabase接続は禁止されています');
      return false;
    } else {
      logger.warn('⚠️  開発環境ですが、リモートSupabaseに切り替えてください');
    }
  }

  return isAvailable;
};

/**
 * Google Maps API キーを取得
 * @returns Google Maps API キー、または undefined
 */
export const getGoogleMapsApiKey = (): string | undefined => {
  return import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
};

/**
 * 環境変数の設定状況をログ出力（デバッグ用）
 */
export const logEnvironmentStatus = (): void => {
  if (!isDebugMode()) return;

  logger.debug('🔧 ラクハイ 環境設定');
  logger.debug('デモモード:', { mode: isDemoMode() ? '有効' : '無効' });
  logger.debug('デバッグモード:', { mode: isDebugMode() ? '有効' : '無効' });
  logger.debug('Gemini API:', { status: getGeminiApiKey() ? '設定済み' : '未設定' });

  if (!isDemoMode()) {
    const supabase = getSupabaseConfig();
    logger.debug('Supabase URL:', { status: supabase.url ? '設定済み' : '未設定' });
    logger.debug('Supabase Key:', { status: supabase.anonKey ? '設定済み' : '未設定' });
  }
};

/**
 * 必要な環境変数が設定されているかチェック
 * @returns 環境変数の検証結果
 */
export const validateEnvironment = (): {
  isValid: boolean;
  missing: string[];
  mode: 'demo' | 'production';
} => {
  const missing: string[] = [];
  const mode = isDemoMode() ? 'demo' : 'production';

  // 共通で必要な環境変数
  if (!getGeminiApiKey()) {
    missing.push('VITE_GEMINI_API_KEY');
  }

  // 本番モード時に必要な環境変数
  if (!isDemoMode()) {
    const supabase = getSupabaseConfig();
    if (!supabase.url) missing.push('VITE_SUPABASE_URL');
    if (!supabase.anonKey) missing.push('VITE_SUPABASE_ANON_KEY');
  }

  return {
    isValid: missing.length === 0,
    missing,
    mode,
  };
};

/**
 * FL-563: マイスケジュール機能（driver_schedules）による検証を使用するかどうかを判定
 *
 * 目的: workingDaysからdriver_schedulesへの段階的な移行
 *
 * リリース計画:
 * - Week 1: ステージング環境でON
 * - Week 2: 本番環境でON
 * - Week 3: Feature Flag削除
 *
 * @returns true: driver_schedules検証使用, false: workingDays検証使用（後方互換）
 */
export const useDriverSchedulesValidation = (): boolean => {
  const enabled = import.meta.env.VITE_USE_DRIVER_SCHEDULES_VALIDATION;
  // デフォルトはtrue（driver_schedules検証を使用）
  return enabled !== 'false';
};
